import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, FlaskConical, Loader2, UserCog } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { FunctionalSelect, SelectItem } from './ui/Select';
import { getRoleCatalog } from '../services/rolesService';
import { api } from '../services/authService';
import { getCookie, setCookie, deleteCookie } from '../services/cookieService';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';
import { membershipsOf, firstAllowedPath, resolveSelectedOrgId } from '../services/permissions';

/** Same cookie the backend reads (ROLE_OVERRIDE_COOKIE); JSON {"roles": [...], "bulk_contributor": bool}. */
export const ROLE_OVERRIDE_COOKIE = 'cube_role_override';
/** Backend IMPERSONATE_COOKIE: the id of the user a platform admin is acting as. */
export const IMPERSONATE_COOKIE = 'cube_impersonate';
const ADMIN_USERS_LIST_COOKIE = 'admin_users_list';
const COLLAPSED_KEY = 'adminToolbarCollapsed';

function readOverride() {
  try {
    const raw = getCookie(ROLE_OVERRIDE_COOKIE);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed?.roles)) return null;
    return { roles: parsed.roles, bulk: Boolean(parsed.bulk_contributor) };
  } catch {
    return null;
  }
}

/** Drop every per-user cache so the next load reflects whoever we are now acting as. */
function clearActingUserCaches() {
  organizationsService.clearOrganizations();
  deleteCookie(ADMIN_USERS_LIST_COOKIE);
}

const userLabel = (u) => {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return name ? `${name} · ${u.email}` : u.email;
};

/**
 * Type-to-filter picker for the user to impersonate. Matches name or email, shows up to 50
 * matches, and supports arrow keys / Enter / Escape.
 */
function UserTypeahead({ users, onPick, disabled }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (
    q
      ? users.filter((u) =>
          [u.email, u.first_name, u.last_name, `${u.first_name || ''} ${u.last_name || ''}`].some((v) =>
            (v || '').toLowerCase().includes(q)
          )
        )
      : users
  ).slice(0, 50);

  const pick = (u) => {
    setOpen(false);
    setQuery('');
    onPick(String(u.id));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && matches[active]) {
      e.preventDefault();
      pick(matches[active]);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Impersonate a user"
        autoComplete="off"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={users.length === 0 ? 'Loading users…' : 'Impersonate: type a name or email'}
        className="h-8 w-64 rounded-lg border border-hairline bg-surface px-2 text-xs text-[#18181B] focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 disabled:opacity-50"
      />
      {open && !disabled ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 max-h-60 w-80 overflow-auto rounded-lg border border-hairline bg-surface p-1 text-[#18181B] shadow-card"
        >
          {matches.length === 0 ? (
            <li className="px-2 py-1.5 text-zinc-400">No matching users</li>
          ) : (
            matches.map((u, i) => (
              <li key={u.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(u)}
                  className={`block w-full truncate rounded-md px-2 py-1.5 text-left ${i === active ? 'bg-[#E8EEF5]' : ''}`}
                >
                  {userLabel(u)}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** Whether the acting session is overridden, and a label for it (shared with the sidebar toggle). */
function adminToolbarMode(user) {
  const active = Array.isArray(user?.role_override);
  const impersonating = Boolean(user?.impersonating);
  let modeLabel = 'Admin test bar';
  if (impersonating && active) modeLabel = 'Impersonating + viewing as role';
  else if (impersonating) modeLabel = 'Impersonating';
  else if (active) modeLabel = 'Viewing as role';
  return { highlighted: active || impersonating, modeLabel };
}

const COLLAPSED_EVENT = 'admin-toolbar-collapsed-change';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Hidden state is per browser; the bar and its sidebar toggle stay in sync through an event. */
function useAdminToolbarCollapsed() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => {
    const sync = () => setCollapsed(readCollapsed());
    window.addEventListener(COLLAPSED_EVENT, sync);
    return () => window.removeEventListener(COLLAPSED_EVENT, sync);
  }, []);
  const set = (next) => {
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* per-browser convenience only */
    }
    setCollapsed(next);
    window.dispatchEvent(new Event(COLLAPSED_EVENT));
  };
  return [collapsed, set];
}

/**
 * Show/hide control for the test bar, at the far left of the bar (or of the slim strip left when
 * it is hidden). Amber while impersonating or viewing as a role, so an override can't
 * be forgotten behind a hidden bar.
 */
export function AdminToolbarToggle() {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useAdminToolbarCollapsed();
  if (!user?.can_switch_roles) return null;
  const { highlighted, modeLabel } = adminToolbarMode(user);
  const label = collapsed
    ? `${highlighted ? `${modeLabel} — ` : ''}Show the admin test bar`
    : 'Hide the admin test bar';
  return (
    <button
      type="button"
      onClick={() => setCollapsed(!collapsed)}
      title={label}
      aria-label={label}
      aria-pressed={!collapsed}
      data-testid="admin-toolbar-toggle"
      className={`inline-flex h-5 w-5 items-center justify-center rounded ${
        highlighted
          ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
          : collapsed
          ? 'text-zinc-400 hover:bg-[#E8EEF5] hover:text-zinc-600'
          : 'bg-[#E8EEF5] text-zinc-600'
      }`}
    >
      <FlaskConical className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * Platform-admin test bar: switch the selected organization, impersonate another account, and
 * "view as" a role set (with or without the bulk-contributor flag). The choices go to the
 * backend as cookies, which swaps the acting user / drops the admin bypass for the session, so
 * pages, buttons and 403s behave exactly as they would for that person or role.
 */
function AdminToolbar() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [draft, setDraft] = useState(() => readOverride()?.roles || []);
  // Stand-in for users.is_bulk_contributor while viewing as a role set.
  const [draftBulk, setDraftBulk] = useState(() => readOverride()?.bulk || false);
  const [applying, setApplying] = useState(false);
  const [orgId, setOrgId] = useState(() => organizationsService.getSelectedOrganization('id'));
  const [users, setUsers] = useState([]);
  const [collapsed] = useAdminToolbarCollapsed();

  const active = Array.isArray(user?.role_override);
  const impersonating = user?.impersonating || null;
  const memberships = membershipsOf(user);
  const orgs = memberships.map((m) => ({ id: String(m.organization_id), name: m.organization_name }));
  const selectedOrgId = resolveSelectedOrgId(user, orgId);
  const canSwitch = Boolean(user?.can_switch_roles);
  // The users list is admin-only on the backend, so load it only while we really are the admin.
  const canListUsers = canSwitch && !impersonating && Boolean(user?.is_admin);

  useEffect(() => {
    if (!canSwitch) return undefined;
    getRoleCatalog().then(setCatalog).catch(() => setCatalog({ roles: [] }));
    const sync = () => setOrgId(organizationsService.getSelectedOrganization('id'));
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, sync);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, sync);
  }, [canSwitch]);

  useEffect(() => {
    if (!canListUsers) return undefined;
    let cancelled = false;
    api
      .get('/api/v2/users/')
      .then((res) => {
        if (cancelled) return;
        const rows = (Array.isArray(res.data) ? res.data : []).filter((u) => u.is_active && String(u.id) !== String(user?.id));
        rows.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setUsers(rows);
      })
      .catch(() => !cancelled && setUsers([]));
    return () => {
      cancelled = true;
    };
  }, [canListUsers, user?.id]);

  if (!canSwitch) return null;

  const finishSwitch = async () => {
    const next = await reloadUser();
    // Land on a page the acting user / role can see.
    navigate(firstAllowedPath(next, resolveSelectedOrgId(next, organizationsService.getSelectedOrganization('id'))), {
      replace: true,
    });
  };

  const applyRoles = async (roles, bulk = false) => {
    setApplying(true);
    try {
      if (roles === null) deleteCookie(ROLE_OVERRIDE_COOKIE);
      else setCookie(ROLE_OVERRIDE_COOKIE, JSON.stringify({ roles, bulk_contributor: bulk }), 1);
      await finishSwitch();
    } finally {
      setApplying(false);
    }
  };

  const impersonate = async (targetId) => {
    setApplying(true);
    try {
      // Impersonation replaces any role override: we want exactly what that person sees.
      deleteCookie(ROLE_OVERRIDE_COOKIE);
      setDraft([]);
      setDraftBulk(false);
      if (targetId) setCookie(IMPERSONATE_COOKIE, String(targetId), 1);
      else deleteCookie(IMPERSONATE_COOKIE);
      clearActingUserCaches();
      await finishSwitch();
    } finally {
      setApplying(false);
    }
  };

  const toggleRole = (key) => {
    setDraft((prev) => (prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]));
  };

  const handleOrgChange = (value) => {
    const parsed = organizationsService.parseOrganizationOptionValue(value);
    const nextId = parsed ? parsed.id : value;
    organizationsService.setSelectedOrganization(nextId, 'id', orgs);
  };

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(user.role_override || []) ||
    draftBulk !== (active ? Boolean(user.is_bulk_contributor) : false);

  const chipClass = (on, tone = 'blue') =>
    `rounded-full border px-2 py-0.5 ${
      on
        ? tone === 'amber'
          ? 'border-amber-600 bg-amber-600 text-white'
          : 'border-[#0062FB] bg-[#0062FB] text-white'
        : 'border-hairline bg-surface text-zinc-600 hover:bg-[#E8EEF5]'
    }`;

  const { highlighted, modeLabel } = adminToolbarMode(user);

  // Hidden: a slim strip keeps the toggle (far left) so the bar can always be brought back.
  if (collapsed) {
    return (
      <div
        className={`flex items-center border-b px-4 py-0.5 md:px-8 ${
          highlighted ? 'border-amber-300 bg-amber-50' : 'border-hairline bg-[#F4F7FB]'
        }`}
        data-testid="admin-toolbar-collapsed"
      >
        <AdminToolbarToggle />
        {highlighted ? <span className="ml-2 text-[11px] font-medium text-amber-900">{modeLabel}</span> : null}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2 text-xs md:px-8 ${
        highlighted ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-hairline bg-[#F4F7FB] text-zinc-600'
      }`}
      data-testid="admin-toolbar"
    >
      <AdminToolbarToggle />
      <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide">
        {highlighted ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {modeLabel}
      </span>

      {impersonating ? (
        <span className="inline-flex items-center gap-1.5">
          <UserCog className="h-3.5 w-3.5" />
          <span className="font-medium">{impersonating.name || impersonating.email}</span>
          <span className="opacity-70">({impersonating.email})</span>
          <button
            type="button"
            onClick={() => impersonate(null)}
            disabled={applying}
            className="ml-1 rounded-md border border-amber-400 px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            Stop impersonating
          </button>
        </span>
      ) : canListUsers ? (
        <UserTypeahead users={users} onPick={impersonate} disabled={applying || users.length === 0} />
      ) : null}

      <FunctionalSelect
        label="Organization"
        className="w-56"
        triggerClassName="h-8 text-xs"
        value={organizationsService.getSelectedOptionValue(orgs, selectedOrgId, 'id')}
        onValueChange={handleOrgChange}
        placeholder="Select org"
        disabled={orgs.length === 0}
      >
        {orgs.map((o) => (
          <SelectItem key={o.id} value={organizationsService.orgToOptionValue(o)}>
            {o.name}
          </SelectItem>
        ))}
      </FunctionalSelect>

      <div className="flex flex-wrap items-center gap-1">
        {(catalog?.roles || []).map((role) => (
          <button
            key={role.key}
            type="button"
            onClick={() => toggleRole(role.key)}
            title={role.description}
            className={chipClass(draft.includes(role.key))}
          >
            {role.label}
          </button>
        ))}
        <span className="mx-1 h-4 border-l border-current opacity-30" aria-hidden="true" />
        <button
          type="button"
          onClick={() => setDraftBulk((v) => !v)}
          title="users.is_bulk_contributor: may submit bulk uploads (FastCube) and run FastID, on top of org roles"
          className={chipClass(draftBulk, 'amber')}
        >
          Bulk contributor flag
        </button>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => applyRoles(draft, draftBulk)}
          disabled={applying || !dirty}
          className="inline-flex items-center gap-1 rounded-md bg-[#0062FB] px-3 py-1 font-medium text-white hover:bg-[#0052E0] disabled:opacity-50"
        >
          {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {draft.length === 0 && !draftBulk ? 'View with no roles' : 'View as'}
        </button>
        {active ? (
          <button
            type="button"
            onClick={() => {
              setDraft([]);
              setDraftBulk(false);
              applyRoles(null);
            }}
            disabled={applying}
            className="rounded-md border border-amber-400 px-3 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            {impersonating ? 'Back to their roles' : 'Back to admin'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default AdminToolbar;
